import { useState, useCallback } from 'react';
import { operatorApi } from '../services/api';
import { Meta, Operator } from '../types';
import { unwrapListResponse, unwrapMetaResponse } from '../apiUnwrapper/apiUnwrapper';

const DEFAULT_META: Meta = { total: 0, size: 5, page: 1 };

/**
 * Manages the operator list and pagination shown in the main page table.
 */
export function useOperatorData() {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [meta, setMeta]           = useState<Meta>(DEFAULT_META);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const loadOperators = useCallback(async (page = 1, size = 5) => {
    try {
      setLoading(true);
      setError(null);
      const res = await operatorApi.getOperators(page, size);
      setOperators(unwrapListResponse<Operator>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      setError('Failed to load operators.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchOperators = useCallback(async (searchTerm: string, page = 1, size = 5) => {
    try {
      setLoading(true);
      setError(null);
      const res = await operatorApi.getAllOperatorsForSearchTerm(searchTerm, page, size);
      setOperators(unwrapListResponse<Operator>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      setError('Failed to search operators.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { operators, meta, loading, error, setError, loadOperators, searchOperators };
}